import React, { useState } from "react";
import OutletPicker from "./OutletPicker";

// "Today" as a bare YYYY-MM-DD built from LOCAL calendar parts. Deliberately not
// `toISOString().slice(0,10)`, which formats in UTC and is a day ahead for US-timezone users in the
// evening — that would let the picker offer a "today" the server (started/ended must be in the past)
// would then reject.
const localTodayDate = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = `${now.getMonth() + 1}`.padStart(2, "0");
  const day = `${now.getDate()}`.padStart(2, "0");
  return `${year}-${month}-${day}`;
};

// Row editor for a single media job (publication) — mirrors pr_profiles' EditEmployment, swapping the
// company picker for the outlet picker. Tracks only the editable fields; the denormalized outlet slug is
// display-only and reconciled by silentReload after the write. There is no `featured` control: media jobs
// have no `featured` flag (Id::MediaJobPolicy does not allowlist one), so — unlike PR employment ordering
// — there's nothing to toggle here.
function EditPublication({ mediaJob, onConfirm, onCancel, disabled = false }) {
  const [mediaJobData, setMediaJobData] = useState({
    mediaOutletId: mediaJob.mediaOutletId,
    mediaOutletName: mediaJob.mediaOutletName,
    title: mediaJob.title,
    from: mediaJob.from,
    to: mediaJob.to,
  });
  const [outletCreating, setOutletCreating] = useState(false);

  const handleChange = (field) => (value) => {
    setMediaJobData((prev) => ({ ...prev, [field]: value }));
  };

  // The stored value can be a full timestamp (id_media_jobs columns are `timestamp without time zone`
  // and migrated rows carry non-midnight times), but <input type="date"> needs a bare YYYY-MM-DD, so
  // keep the calendar date verbatim — no Date parsing / timezone conversion, which would shift the day.
  const adjustDateValue = (value) => (value ? value.slice(0, 10) : "");

  // Confirm is gated on a persisted outlet id because the server (Id::MediaJobPolicy) accepts only
  // media_outlet_id — there is no name-based creation to fall back on — and a create may still be in
  // flight (its resolved id is what the row must reference). It's also blocked while a mutation is
  // already in flight, since the single-flight media-jobs hook would silently swallow a second click.
  const confirmDisabled = disabled || outletCreating || !(Number(mediaJobData.mediaOutletId) > 0);

  const confirmChanges = () => {
    onConfirm(mediaJobData);
  };

  const onOutletChange = (outlet) => {
    setMediaJobData((prev) => ({
      ...prev,
      mediaOutletId: outlet.value,
      mediaOutletName: outlet.label,
    }));
  };

  // min/max keep the pickers from offering illogical dates (start in the future, end before start) that
  // the server would reject anyway. They are UX only — server validation + the ServerErrorAlert path stay
  // the enforcement backstop, so an out-of-range pre-existing stored value still renders (attributes never
  // clamp the value).
  const today = localTodayDate();

  return (
    <tr>
      <td>
        <OutletPicker
          mediaOutletName={mediaJobData.mediaOutletName}
          mediaOutletId={mediaJobData.mediaOutletId}
          onOutletChange={onOutletChange}
          onCreatingChange={setOutletCreating}
        />
      </td>
      <td>
        <input
          className="form-control"
          onChange={(e) => handleChange("title")(e.currentTarget.value)}
          value={mediaJobData.title}
        />
      </td>
      <td>
        <input
          className="form-control"
          type="date"
          value={adjustDateValue(mediaJobData.from)}
          max={adjustDateValue(mediaJobData.to) || today}
          onChange={(e) => handleChange("from")(e.currentTarget.value)}
        />
      </td>
      <td>
        <input
          className="form-control"
          type="date"
          value={adjustDateValue(mediaJobData.to)}
          min={adjustDateValue(mediaJobData.from) || undefined}
          max={today}
          onChange={(e) => handleChange("to")(e.currentTarget.value)}
        />
      </td>
      <td className="text-nowrap">
        <div className="d-flex">
          <button
            onClick={confirmChanges}
            type="button"
            className="btn btn-sm btn-outline-primary d-flex"
            disabled={confirmDisabled}
          >
            <i className="fas fa-check" />
          </button>
          &nbsp;
          <button
            onClick={onCancel}
            type="button"
            className="btn btn-sm btn-outline-secondary d-flex"
            disabled={disabled}
          >
            <i className="fas fa-xmark" />
          </button>
        </div>
      </td>
    </tr>
  );
}

export default EditPublication;
